import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface CartDetail {
  id: number | string;
  detailId: number;
  name: string;
  code: string;
  price: number | string;
  basePrice?: number | string;
  quantity: number;
  subtotal: number | string;
  total: number | string;
  discount: number;
  discountMethod: string;
  taxPercent: number;
  taxMethod: string;
  unitId?: number | string;
  unitLabel?: string;
  currentstock: number;
  current?: number;
  qteSale?: number;
  productType?: string;
  productBatches?: any[];
  [key: string]: unknown;
}

export interface CartUnit {
  label: string;
  id: number | string;
  operator: string;
  operatorValue: number;
  name?: string;
  description?: string;
  code?: string;
}

export interface PosSessionState {
  details: CartDetail[];
  sale: {
    warehouseId: string;
    customerId: string;
    taxRate: number;
    shipping: number;
    discount: number | string;
    discountMethod?: string;
    currencyId?: string | number;
    exchangeRate?: string;
    draftSaleId?: string | null;
    draftRef?: string;
    taxNet: number;
    taxMethod: string;
    notes: string;
  };
  units: CartUnit[][];
  currentDetailIndex: number | null;
  unitSearchQuery: string;
  filteredUnits: CartUnit[];
  selectedUnit: CartUnit | null;
  showUnitModal: boolean;
  load_product: boolean;
  /** Ref of the last ticket queued for /sync replay — guards against
   * double-queueing the same cart after a reload (persisted cart restored
   * while its sale is already queued). */
  lastQueuedRef: string | null;
  lastQueuedAt: string | null;
}

const initialState: PosSessionState = {
  details: [],
  sale: {
    warehouseId: '',
    customerId: '',
    taxRate: 0,
    shipping: 0,
    discount: 0,
    taxNet: 0,
    taxMethod: 'exclusive',
    notes: '',
  },
  units: [],
  currentDetailIndex: null,
  unitSearchQuery: '',
  filteredUnits: [],
  selectedUnit: null,
  showUnitModal: false,
  load_product: true,
  lastQueuedRef: null,
  lastQueuedAt: null,
};

export const posSessionSlice = createSlice({
  name: 'posSession',
  initialState,
  reducers: {
    setDetails(state, action: PayloadAction<CartDetail[]>) {
      state.details = action.payload;
    },
    updateDetail(state, action: PayloadAction<{ index: number; detail: Partial<CartDetail> }>) {
      const { index, detail } = action.payload;
      const existing = state.details[index];
      // A stale/out-of-range index (captured before a concurrent
      // removeDetail/resetCart) must not create a sparse hole or spread
      // `undefined` into an incomplete detail object.
      if (!existing) return;
      state.details[index] = { ...existing, ...detail };
    },
    removeDetail(state, action: PayloadAction<number>) {
      const index = action.payload;
      if (index < 0 || index >= state.details.length) return;
      state.details.splice(index, 1);
      // `units` is positional parallel to `details` (units[i] belongs to
      // details[i]) — splice it at the same index so a removal here cannot
      // desync the two arrays.
      if (index < state.units.length) {
        state.units.splice(index, 1);
      }
    },
    setSale(state, action: PayloadAction<Partial<PosSessionState['sale']>>) {
      state.sale = { ...state.sale, ...action.payload };
    },
    setUnits(state, action: PayloadAction<{ index: number; units: CartUnit[] }>) {
      // `units` is positional parallel to `details`; an out-of-range index
      // would leave a hole that `removeDetail`'s splice then shifts wrongly.
      const { index, units } = action.payload;
      if (index < 0 || index >= state.units.length) return;
      state.units[index] = units;
    },
    setAllUnits(state, action: PayloadAction<CartUnit[][]>) {
      state.units = action.payload;
    },
    setCurrentDetailIndex(state, action: PayloadAction<number | null>) {
      state.currentDetailIndex = action.payload;
    },
    setUnitSearchQuery(state, action: PayloadAction<string>) {
      state.unitSearchQuery = action.payload;
    },
    setFilteredUnits(state, action: PayloadAction<CartUnit[]>) {
      state.filteredUnits = action.payload;
    },
    setSelectedUnit(state, action: PayloadAction<CartUnit | null>) {
      state.selectedUnit = action.payload;
    },
    setShowUnitModal(state, action: PayloadAction<boolean>) {
      state.showUnitModal = action.payload;
    },
    setLoadProduct(state, action: PayloadAction<boolean>) {
      state.load_product = action.payload;
    },
    resetCart(state) {
      state.sale = { ...initialState.sale, warehouseId: state.sale.warehouseId, customerId: state.sale.customerId };
      state.details = [];
      state.units = [];
      state.load_product = true;
      // Clear the queued marker too: leaving the previous ticket's ref behind
      // makes the next queue call look like a duplicate of a cart that no
      // longer exists.
      state.lastQueuedRef = null;
      state.lastQueuedAt = null;
    },
    markTicketQueued(state, action: PayloadAction<{ ref: string }>) {
      state.lastQueuedRef = action.payload.ref;
      state.lastQueuedAt = new Date().toISOString();
      // Clear the working ticket — the sale lives in the offline queue now.
      // Without this a reload restores the same cart and the cashier can
      // double-queue it with a NEW ref (ref uniqueness cannot dedup it).
      state.details = [];
      state.units = [];
      state.load_product = true;
    },
  },
});

export const {
  setDetails,
  updateDetail,
  removeDetail,
  setSale,
  setUnits,
  setAllUnits,
  setCurrentDetailIndex,
  setUnitSearchQuery,
  setFilteredUnits,
  setSelectedUnit,
  setShowUnitModal,
  setLoadProduct,
  resetCart,
  markTicketQueued,
} = posSessionSlice.actions;

export default posSessionSlice.reducer;
